/**
 * confirmDialog — one promise-based confirm for the whole app (audit X-7).
 *
 * Replaces scattered window.confirm() calls with a themed, accessible modal.
 * Imperative on purpose: call it from any handler (async or not) without
 * threading a hook through every component.
 *
 *   if (!(await confirmDialog('Delete this list? This can't be undone.'))) return;
 *
 *   const ok = await confirmDialog({
 *     title: 'Revoke invitation',
 *     message: `Revoke the invite for ${email}?`,
 *     confirmLabel: 'Revoke',
 *     danger: true,        // default true — red confirm button
 *   });
 *
 * A single <ConfirmHost/> mounted at the app root renders the dialog.
 */

let hostHandler = null;

/** Internal: ConfirmHost registers its opener here on mount. */
export function _registerConfirmHost(fn) {
  hostHandler = fn;
  return () => {
    if (hostHandler === fn) hostHandler = null;
  };
}

/**
 * Show a confirm dialog. Resolves true if confirmed, false if cancelled
 * (Esc, backdrop, or Cancel). Accepts a string (message only) or an options
 * object: { title, message, confirmLabel, cancelLabel, danger }.
 */
export function confirmDialog(opts) {
  const o = typeof opts === 'string' ? { message: opts } : { ...(opts || {}) };
  if (typeof hostHandler !== 'function') {
    // Host not mounted (unit tests, or a surface rendered outside the app
    // root). Degrade to the native prompt rather than silently swallowing a
    // user-initiated confirm.
    // eslint-disable-next-line no-alert
    return Promise.resolve(window.confirm(o.message || 'Are you sure?'));
  }
  return hostHandler(o);
}

/**
 * Show a text-input dialog. Resolves the entered string on confirm, or null
 * if cancelled (Esc, backdrop, or Cancel). Accepts a string (message only)
 * or an options object:
 *   { title, message, placeholder, defaultValue, confirmLabel, cancelLabel,
 *     required }   // required defaults true — confirm disabled while empty
 *
 *   const name = await promptDialog({ title: 'New list', placeholder: 'List name' });
 *   if (name === null) return; // cancelled
 */
export function promptDialog(opts) {
  const o = typeof opts === 'string' ? { message: opts } : { ...(opts || {}) };
  o.input = {
    placeholder: o.placeholder || '',
    defaultValue: o.defaultValue || '',
    required: o.required !== false,
  };
  o.danger = o.danger === true; // prompts are creative, not destructive — default primary
  if (typeof hostHandler !== 'function') {
    // eslint-disable-next-line no-alert
    return Promise.resolve(window.prompt(o.message || o.title || '', o.input.defaultValue));
  }
  return hostHandler(o);
}

export default confirmDialog;
